import { LOCALE_ID, inject, provideAppInitializer, type EnvironmentProviders } from '@angular/core';
import { setNumberLocale } from './value-formatter.service';

/**
 * The bootstrap step itself, named so a test can run the same one.
 *
 * Extracted rather than inlined in the provider because `TestBed` does not run
 * app initializers: a spec that wants a formatter reading its own `LOCALE_ID`
 * has to perform this step, and a spec that re-implemented it would drift from
 * the bootstrap the moment this changed — which is how a locale test ends up
 * proving nothing while looking thorough.
 *
 * Must be called in an injection context.
 */
export function adoptLocaleIdAsNumberLocale(): void {
  setNumberLocale(inject(LOCALE_ID));
}

/**
 * Hand the formatting layer the deployment's locale, so `LOCALE_ID` is the one
 * answer to "which locale did this number render in" rather than one of
 * several.
 *
 * `ValueFormatterService` cannot inject the token itself: `pricing-format.ts`,
 * `chart-configs.ts` and `insight-chart-mapping.ts` each construct one with
 * `new` at module scope, outside any injection context, where `inject()`
 * throws NG0203. Copying the token into a module value from an initializer
 * reaches those instances too — the same shape `UiConfigService` uses to push
 * the tenant's price knobs into the same module.
 *
 * Synchronous and dependency-free, so it settles before anything can render a
 * number, and it cannot delay first paint.
 */
export function provideNumberLocaleFromLocaleId(): EnvironmentProviders {
  return provideAppInitializer(adoptLocaleIdAsNumberLocale);
}
